import axios from 'axios';
import {
    authRequest,
    stuffAdded,
    authSuccess,
    authFailed,
    authLogout,
    doneSuccess,
    getDeleteSuccess,
    getRequest,
    getFailed,
    getError,
    patchCurrentUser,
} from './userSlice';
import {
    buildApiUrl,
    formatApiUnreachableMessage,
    GENERIC_CONNECTION_ERROR,
    getApiBaseUrl,
    getSupabaseAnonKey,
} from '../../config/api';
import { normalizeStaffRole } from '../../utils/staffRoles';

function loginRegisterErrorMessage(error, baseUrl) {
    if (error.response?.data?.message) {
        return error.response.data.message;
    }
    if (error.response?.data && typeof error.response.data === "string") {
        return error.response.data;
    }
    if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        return formatApiUnreachableMessage(baseUrl);
    }
    const msg = String(error.response?.data?.message || error.message || "");
    if (
        (/missing authorization header/i.test(msg) || error.response?.status === 401) &&
        /supabase\.co\/functions/i.test(baseUrl) &&
        !getSupabaseAnonKey()
    ) {
        if (process.env.NODE_ENV === "development") {
            console.warn(
                "[api] Missing REACT_APP_SUPABASE_ANON_KEY in frontend/.env (Project Settings → API → anon public).",
            );
        }
        return GENERIC_CONNECTION_ERROR;
    }
    const rawMessage = String(error.message || "").trim();
    if (/https?:\/\//i.test(rawMessage)) {
        return "Request failed. Please try again.";
    }
    return rawMessage || "Request failed";
}

export const loginUser = (fields, role) => async (dispatch) => {
    dispatch(authRequest());
    const baseUrl = getApiBaseUrl();

    try {
        const result = await axios.post(`${baseUrl}/${role}Login`, fields, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (result.data.role) {
            dispatch(authSuccess(result.data));
        } else {
            dispatch(authFailed(result.data.message));
        }
    } catch (error) {
        dispatch(authFailed(loginRegisterErrorMessage(error, baseUrl)));
    }
};

/** Single login endpoint — backend detects Admin, Teacher, or Student from credentials */
export const loginUserUnified = (fields) => async (dispatch) => {
    dispatch(authRequest());
    const baseUrl = getApiBaseUrl();

    try {
        const result = await axios.post(`${baseUrl}/Login`, fields, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data?.role) {
            dispatch(authSuccess(result.data));
        } else {
            dispatch(authFailed(result.data?.message || "Login failed"));
        }
    } catch (error) {
        dispatch(authFailed(loginRegisterErrorMessage(error, baseUrl)));
    }
};

export const registerUser = (fields, role, photoFile) => async (dispatch) => {
    dispatch(authRequest());
    const baseUrl = getApiBaseUrl();

    try {
        let result;
        if (role === "Student" && photoFile instanceof File) {
            const fd = new FormData();
            Object.entries(fields).forEach(([key, value]) => {
                if (value === undefined || value === null) return;
                if (Array.isArray(value)) {
                    fd.append(key, JSON.stringify(value));
                } else {
                    fd.append(key, String(value));
                }
            });
            fd.append("photo", photoFile, photoFile.name);
            result = await axios.post(`${baseUrl}/${role}Reg`, fd);
        } else {
            result = await axios.post(`${baseUrl}/${role}Reg`, fields, {
                headers: { 'Content-Type': 'application/json' },
            });
        }
        const hasEntityId = Boolean(result.data?._id || result.data?.id);
        const isErrorMessage =
            result.data?.message &&
            !result.data?.role &&
            !hasEntityId;

        if (isErrorMessage) {
            dispatch(authFailed(result.data.message));
        } else if (result.data.schoolName) {
            dispatch(authSuccess(result.data));
        } else if (
            result.data.role === "Teacher" ||
            result.data.staffRole ||
            result.data.role === "Student" ||
            hasEntityId
        ) {
            dispatch(stuffAdded(result.data));
            return result.data;
        } else if (result.data.school) {
            dispatch(stuffAdded(result.data));
            return result.data;
        } else {
            dispatch(authFailed(result.data.message || "Registration failed"));
        }
    } catch (error) {
        const data = error.response?.data;
        let msg = loginRegisterErrorMessage(error, baseUrl);
        if (typeof data === "object" && data?.message) {
            msg = data.message;
        } else if (typeof data === "string" && data.trim()) {
            msg = data;
        } else if (
            /no number after minus sign in json/i.test(error.message || "")
        ) {
            msg =
                "The server could not read the registration request. If you attached a photo, try again; otherwise contact support.";
        }
        dispatch(authFailed(msg));
    }
    return null;
};

export const logoutUser = () => (dispatch) => {
    dispatch(authLogout());
};

function buildStudentSessionPatch(student) {
    if (!student || typeof student !== "object" || student.message) return null;
    return {
        name: student.name,
        rollNum: student.rollNum,
        sclassName: student.sclassName,
        school: student.school,
        admissionStatus: student.admissionStatus,
        portalEnabled: student.portalEnabled,
        enrollmentStatus: student.enrollmentStatus,
        photoUrl: student.photoUrl,
    };
}

export const getUserDetails = (id, address) => async (dispatch, getState) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/${address}/${id}`);
        if (result.data) {
            dispatch(doneSuccess(result.data));
            if (address === "Student") {
                const { currentUser } = getState().user;
                const userId = currentUser?._id || currentUser?.id;
                if (userId && String(userId) === String(id)) {
                    const patch = buildStudentSessionPatch(result.data);
                    if (patch) dispatch(patchCurrentUser(patch));
                }
            }
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

// export const deleteUser = (id, address) => async (dispatch) => {
//     dispatch(getRequest());

//     try {
//         const result = await axios.delete(`${process.env.REACT_APP_BASE_URL}/${address}/${id}`);
//         if (result.data.message) {
//             dispatch(getFailed(result.data.message));
//         } else {
//             dispatch(getDeleteSuccess());
//         }
//     } catch (error) {
//         dispatch(getError(error));
//     }
// }


const DELETE_ROUTE = {
    Student: (id) => `Student/${id}`,
    Students: (id) => `Students/${id}`,
    StudentsClass: (id) => `StudentsClass/${id}`,
    Teacher: (id) => `Teacher/${id}`,
    Teachers: (id) => `Teachers/${id}`,
    TeachersClass: (id) => `TeachersClass/${id}`,
    Notice: (id) => `Notice/${id}`,
    Notices: (id) => `Notices/${id}`,
    Sclass: (id) => `Sclass/${id}`,
    Sclasses: (id) => `Sclasses/${id}`,
    Subject: (id) => `Subject/${id}`,
    Subjects: (id) => `Subjects/${id}`,
    SubjectsClass: (id) => `SubjectsClass/${id}`,
};

export const deleteUser = (id, address) => async (dispatch) => {
    dispatch(getRequest());
    const baseUrl = getApiBaseUrl();
    const buildPath = DELETE_ROUTE[address];
    if (!buildPath) {
        dispatch(getFailed(`Delete is not configured for "${address}".`));
        return { success: false };
    }

    try {
        const result = await axios.delete(`${baseUrl}/${buildPath(id)}`);
        const data = result.data;
        const hasEntity = Boolean(data?.id || data?._id);
        const hasDeletedCount = typeof data?.deletedCount === "number";
        if (data?.message && !hasEntity && !hasDeletedCount) {
            dispatch(getFailed(data.message));
            return { success: false };
        }
        if (hasDeletedCount && data.deletedCount === 0 && data?.message) {
            dispatch(getFailed(data.message));
            return { success: false };
        }
        dispatch(getDeleteSuccess());
        return { success: true };
    } catch (error) {
        const msg =
            error.response?.data?.message ||
            error.message ||
            "Delete failed";
        dispatch(getError(msg));
        return { success: false, error: msg };
    }
};

/** Reload assigned subjects from the server (after admin assigns new subjects). */
export const refreshTeacherPortalUser = (teacherId) => async (dispatch, getState) => {
    if (!teacherId) return { success: false };

    try {
        const result = await axios.get(`${getApiBaseUrl()}/Teacher/${teacherId}`);
        const data = result.data;
        const hasEntity = Boolean(data?.id || data?._id);
        if (data?.message && !hasEntity) {
            return { success: false, message: data.message };
        }
        if (!hasEntity) return { success: false };

        const prev = getState()?.user?.currentUser || {};
        dispatch(
            patchCurrentUser({
                teachSubjects: data.teachSubjects ?? prev.teachSubjects,
                teachSubject: data.teachSubject ?? prev.teachSubject,
                teachSclass: data.teachSclass ?? prev.teachSclass,
                classTeacherOf: data.classTeacherOf ?? prev.classTeacherOf,
                profileCompleted: data.profileCompleted ?? prev.profileCompleted,
                portalEnabled: data.portalEnabled ?? prev.portalEnabled,
                name: data.name ?? prev.name,
            }),
        );
        return { success: true, data };
    } catch {
        return { success: false };
    }
};

export const updateStudentSelfProfile = (fields, id) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(buildApiUrl(`/Student/${id}/profile`), fields, {
            headers: { 'Content-Type': 'application/json' },
        });
        const data = result.data;
        const hasEntity = Boolean(data?.id || data?._id);
        if (data?.message && !hasEntity) {
            dispatch(authFailed(data.message));
            return { ok: false, message: data.message };
        }
        if (hasEntity) {
            const portalUser = { ...data, role: "Student" };
            dispatch(authSuccess(portalUser));
            return { ok: true, data: portalUser };
        }
        dispatch(authFailed("Could not save profile"));
        return { ok: false, message: "Could not save profile" };
    } catch (error) {
        const msg =
            error.response?.data?.message ||
            error.message ||
            'Could not save profile';
        dispatch(getError(msg));
        return { ok: false, message: msg };
    }
};

export const updateTeacherSelfProfile = (fields, id) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(buildApiUrl(`/Teacher/${id}/profile`), fields, {
            headers: { 'Content-Type': 'application/json' },
        });
        const data = result.data;
        const hasEntity = Boolean(data?.id || data?._id);
        if (data?.message && !hasEntity) {
            dispatch(authFailed(data.message));
            return { ok: false, message: data.message };
        }
        if (hasEntity) {
            const portalUser = {
                ...data,
                role: "Teacher",
                staffRole: data.staffRole ?? normalizeStaffRole(data.role),
            };
            dispatch(authSuccess(portalUser));
            return { ok: true, data: portalUser };
        }
        dispatch(authFailed("Could not save profile"));
        return { ok: false, message: "Could not save profile" };
    } catch (error) {
        const msg =
            error.response?.data?.message ||
            error.message ||
            'Could not save profile';
        dispatch(getError(msg));
        return { ok: false, message: msg };
    }
};

export const updateUser = (fields, id, address) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(`${getApiBaseUrl()}/${address}/${id}`, fields, {
            headers: { 'Content-Type': 'application/json' },
        });
        const data = result.data;
        const hasEntity = Boolean(data?.id || data?._id);
        if (data?.message && !hasEntity) {
            dispatch(authFailed(data.message));
            return;
        }
        if (data?.schoolName || data?.role === "Teacher") {
            dispatch(
                authSuccess({
                    role: address === "Admin" ? "Admin" : data.role,
                    ...data,
                }),
            );
        } else {
            dispatch(doneSuccess(data));
        }
    } catch (error) {
        const msg =
            error.response?.data?.message ||
            error.message ||
            'Update failed';
        dispatch(getError(msg));
    }
}

export const addStuff = (fields, address) => async (dispatch) => {
    dispatch(authRequest());
    const baseUrl = getApiBaseUrl();

    try {
        const result = await axios.post(`${baseUrl}/${address}Create`, fields, {
            headers: { 'Content-Type': 'application/json' },
        });

        if (result.data.message) {
            dispatch(authFailed(result.data.message));
        } else {
            dispatch(stuffAdded(result.data));
        }
    } catch (error) {
        dispatch(authFailed(loginRegisterErrorMessage(error, baseUrl)));
    }
};